import {Component, OnInit, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ConfirmationService, FilterMetadata, MessageService, SelectItem} from 'primeng/api';
import {Table} from 'primeng/table';
import {Observable} from 'rxjs';
import {datedPriceSort, doPricesOverlap} from '../../helpers/hardwareHelpers';
import {sortByLabel} from '../../helpers/helperFunctions';
import {numberOnly} from '../../helpers/keyboardHelpers';
import {BrandCfg, getBrandConfigs, getBrandSelectItems} from '../../lookups/brands';
import {hardwareSetCategories} from '../../lookups/hardwareCategories';
import {Column} from '../../models/column.model';
import {DatedPrice} from '../../models/datedPrice.model';
import {Hardware} from '../../models/hardware.model';
import {HardwareSet} from '../../models/hardwareSet.model';
import {HardwareId} from '../../models/mongoose-populated-ids/hardwareId.model';
import {MultiRecordResponse} from '../../models/responses/multiRecordResponse.model';
import {SingleRecordResponse} from '../../models/responses/singleRecordResponse.model';
import {HardwareService} from '../hardware.service';

interface HardwareSetRow extends HardwareSet {
  'newFromDate'?: string;
  'newToDate'?: string;
  'newQuarterly'?: number;
  'newAnnual'?: number;
  'newHardware'?: Hardware;
}

@Component({
  selector: 'app-hardware-set',
  templateUrl: './hardware-set.component.html',
  styleUrls: ['./hardware-set.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class HardwareSetComponent implements OnInit {

  constructor(
    private hardwareService: HardwareService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private title: Title,
  ) { }

  @ViewChild('dt', {static: true})
  private table: Table;
  numberOnly = numberOnly;
  brandConfigs: BrandCfg;
  brands: SelectItem<string>[];
  hardwareSetCategories: SelectItem<string>[] = hardwareSetCategories;
  selectedCategories: string[];
  selectedBrand: string;
  hardwareSets: HardwareSetRow[] = [];
  hardwareList: SelectItem<Hardware>[] = [];
  cols: Column[] = [
    { field: 'title', header: 'Title' },
    { field: 'overridePlanSymbol', header: 'Plan Symbol' },
    { field: 'containedHardware', header: 'Hardware in Set' },
    { field: 'overridePricesExVat', header: 'Prices Ex VAT' },
    { field: 'overrideReplacementPrice', header: 'Replacement Price' },
    { field: 'websiteId', header: 'Brand' },
    { field: 'category', header: 'Category' },
    { field: 'updated', header: 'Updated' },
    { field: 'actions', header: 'Actions' },
  ];
  canAddHardwareSet: boolean = false;

  ngOnInit(): void {
    this.title.setTitle('CRM Hardware Sets');
    this.brandConfigs = getBrandConfigs();
    this.brands = getBrandSelectItems();
  }

  loadHardwareSets(): void {
    if (!this.selectedBrand || !this.brandConfigs[this.selectedBrand]) {
      return;
    }
    const websiteId: string = this.brandConfigs[this.selectedBrand]._id;
    if (!websiteId) {
      return;
    }
    this.canAddHardwareSet = false;
    this.hardwareService.getHardwareSetsForSite(websiteId)
      .subscribe((response: MultiRecordResponse<HardwareSet>) => {
        if (response.success) {
          this.hardwareSets = response.data.map((hardwareSet: HardwareSet) => {
            hardwareSet.overridePricesExVat.sort(datedPriceSort);
            return hardwareSet;
          });
          this.canAddHardwareSet = true;
        } else {
          this.showErrorPopUp('Error', response.message || 'Something went wrong try again.');
        }
      }, (err: Error) => {
        console.log('ERROR while getting hardware sets: ', err);
        this.showErrorPopUp('Error', 'Something went wrong try again.');
      });
    this.hardwareService.getHardwareForSite(websiteId)
      .subscribe((response: MultiRecordResponse<Hardware>) => {
        if (response.success) {
          // Don't allow monitoring only hardware to be in sets
          this.hardwareList = response.data
            .filter((hardware: Hardware) => 
              hardware.category != 'Monitoring Only'
            )
            .map((hardware: Hardware) => {
              return {
                'label': hardware.title,
                'value': hardware,
              }
            });
          sortByLabel(this.hardwareList);
        } else {
          this.showErrorPopUp('Error', response.message || 'Something went wrong try again.');
        }
      }, (err: Error) => {
        console.log('ERROR while getting hardware: ', err);
        this.showErrorPopUp('Error', 'Something went wrong try again.');
      });
  }

  applyFilter($event: Event, field: string, filterType: string): void {
    this.table.filter(($event.target as HTMLInputElement).value, field, filterType);
  }

  getFilterValue(field: string): string {
    if (!this.table.filters[field]) {
      return '';
    }
    return (this.table.filters[field] as FilterMetadata).value;
  }

  deletePrice(rowData: HardwareSetRow, index: number): void {
    rowData.overridePricesExVat.splice(index, 1);
  }

  deleteHardware(rowData: HardwareSetRow, index: number): void {
    rowData.containedHardware.splice(index, 1);
  }

  addPrice(rowData: HardwareSetRow): boolean {
    if (!rowData.newFromDate && !rowData.newToDate) {
      this.showInfoPopUp('Missing Dates', 'You must enter at least one date for the price.');
      return false;
    }
    if (!!rowData.newFromDate && !!rowData.newToDate && (rowData.newFromDate >= rowData.newToDate)) {
      this.showInfoPopUp('Invalid Date Range', 'The from date must be before the to date');
      return false;
    }
    let overlappingRange: boolean = false;
    rowData.overridePricesExVat.forEach((datedPrice: DatedPrice) => {
      if (!rowData.newFromDate) {
        if (!datedPrice.fromDate) {
          // New price and an existing price are open on the from date, so must overlap
          overlappingRange = true;
        } else if (datedPrice.fromDate <= rowData.newToDate) {
          overlappingRange = true;
        }
      } else if (!rowData.newToDate) {
        if (!datedPrice.toDate) {
          // New price and an existing price are open on the to date, so must overlap
          overlappingRange = true;
        } else if (datedPrice.toDate >= rowData.newFromDate) {
          overlappingRange = true;
        }
      } else {
        // Both start and end date specified on the new price
        if ((!!datedPrice.fromDate) && (datedPrice.fromDate >= rowData.newFromDate) && (datedPrice.fromDate <= rowData.newToDate) ||
            (!!datedPrice.toDate) && (datedPrice.toDate >= rowData.newFromDate) && (datedPrice.toDate <= rowData.newToDate)) {
          // And one side or other of the existing price's date range is within the new range
          overlappingRange = true;
        } else if ((!!datedPrice.fromDate && (datedPrice.fromDate <= rowData.newFromDate) && (!datedPrice.toDate || (datedPrice.toDate >= rowData.newToDate))) ||
            (!!datedPrice.toDate && (datedPrice.toDate >= rowData.newToDate) && !datedPrice.fromDate)) {
          // The new range fits entirely within an existing range
          overlappingRange = true;
        }
      }
    });
    if (overlappingRange) {
      this.showInfoPopUp('Invalid Date Range', 'The date range you have entered overlaps with an existing range.');
      return false;
    }
    // Must have both prices
    if (!rowData.newQuarterly || !rowData.newAnnual || (rowData.newQuarterly <= 0) || (rowData.newAnnual <= 0)) {
      this.showInfoPopUp('Missing Price', 'You must enter a quarterly and an annual price.');
      return false;
    }

    rowData.overridePricesExVat.push({
      'fromDate': rowData.newFromDate,
      'toDate': rowData.newToDate,
      'quarterly': rowData.newQuarterly,
      'annual': rowData.newAnnual,
    });
    rowData.overridePricesExVat.sort(datedPriceSort);
    delete rowData.newFromDate;
    delete rowData.newToDate;
    delete rowData.newQuarterly;
    delete rowData.newAnnual;
    return true;
  }

  addHardware(rowData: HardwareSetRow): void {
    if (!rowData.newHardware) {
      return;
    }
    (rowData.containedHardware as HardwareId[]).push({
      '_id': rowData.newHardware._id,
      'title': rowData.newHardware.title,
    });
    delete rowData.newHardware;
  }

  validToSave(hardwareSet: HardwareSetRow) {
    const errors: string[] = [];
    if (!hardwareSet.title) {
      errors.push('a title');
    }
    if (!hardwareSet.category) {
      errors.push('a category');
    }
    if (!hardwareSet.overridePlanSymbol) {
      errors.push('a plan symbol');
    } else if (hardwareSet.overridePlanSymbol.toLocaleLowerCase().startsWith('mon')) {
      errors.push('a plan symbol that does not start MON');
    }
    if (Object.keys(hardwareSet.containedHardware).length == 0) {
      errors.push('at least 1 piece of hardware');
    }
    if (Object.keys(hardwareSet.overridePricesExVat).length == 0) {
      errors.push('at least one date range with a price');
    } else {
      let pricesValid: boolean = true;
      let datesValid: boolean = true;
      Object.keys(hardwareSet.overridePricesExVat).forEach((index: string) => {
        if ((hardwareSet.overridePricesExVat[index].quarterly <= 0) || (hardwareSet.overridePricesExVat[index].annual <= 0)) {
          pricesValid = false;
        }
        if (!hardwareSet.overridePricesExVat[index].fromDate && !hardwareSet.overridePricesExVat[index].toDate) {
          datesValid = false;
        }
      });
      if (!pricesValid) {
        errors.push('a quarterly and annual price for each date range');
      }
      if (!datesValid) {
        errors.push('a start and/or end date for each date range');
      }
      if (doPricesOverlap(hardwareSet.overridePricesExVat)) {
        errors.push('date ranges that do not overlap');
      }
    }
    if (errors.length > 0) {
      this.showInfoPopUp('Invalid Information', `You must enter ${errors.join(', ')} before saving.`);
      return false;
    } 
    return true;
  }

  saveHardwareSet(rowData: HardwareSetRow): void {
    if (!rowData.newFromDate && !rowData.newToDate) {
      this.updateHardwareSet(rowData);
    } else {
      this.confirmationService.confirm({
        key: 'save',
        message: 'You have entered a price you have not yet added, do you want it to be added before saving the update?',
        header: 'Price Addition',
        icon: 'pi pi-info-circle',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        rejectVisible: true,
        accept: () => {
          if (this.addPrice(rowData)) {
            this.updateHardwareSet(rowData);  
          }
        },
        reject: () => {
          this.updateHardwareSet(rowData);
        }
      });
    }
  }

  updateHardwareSet(rowData: HardwareSetRow): void {
    if (!this.validToSave(rowData)) {
      return;
    }
    // Clear fields used for adding prices
    delete rowData.newFromDate;
    delete rowData.newToDate;
    delete rowData.newQuarterly;
    delete rowData.newAnnual;
    rowData.updatedBy = localStorage.getItem('userName');
    let response: Observable<SingleRecordResponse<HardwareSet>>;
    if (!!rowData._id) {
      response = this.hardwareService.updateHardwareSet(rowData._id, {'hardwareSet': rowData});
    } else {
      response = this.hardwareService.saveNewHardwareSet({'hardwareSet': rowData});
      this.canAddHardwareSet = true;
    }
    
    response.subscribe((rsp: SingleRecordResponse<HardwareSet>) => {
      if (!rsp.success || !rsp.data) {
        this.showErrorPopUp('Error Updating Hardware Set', rsp.message || 'Something went wrong when trying to update the hardware set. Please try again.');
        this.handleError(rsp.error);
      } else {
        const tempFirst: number = this.table.first;
        this.showSuccess();
        this.hardwareSets = [...[rsp.data], ...this.hardwareSets.filter((hardwareSet: HardwareSet) => 
          !!hardwareSet._id && (hardwareSet._id !== rsp.data._id)
        )];
        this.table.first = tempFirst;
      }
    }, (err: Error) => {
      this.handleError(err);
      this.showErrorPopUp('Error Updating Hardware',
          `Something went wrong when trying to update the hardware. Please try again. Error: ${err.message}`);
    });
  }

  addHardwareSet(): void {
    this.canAddHardwareSet = false;
    const newHardwareSet: HardwareSet = {
      'title': '',
      'overridePlanSymbol': '',
      'overridePricesExVat': [],
      'overrideRecentQuarterlyPrice': 0,
      'overrideRecentAnnualPrice': 0,
      'containedHardware': [],
      'overrideReplacementPrice': 0,
      'websiteId': {
        '_id': this.brandConfigs[this.selectedBrand]._id,
        'title': this.selectedBrand,
        'background': this.brandConfigs[this.selectedBrand].background,
        'color': this.brandConfigs[this.selectedBrand].color,
      },
      'category': '',
      'updatedBy': localStorage.getItem('userName'),
      'createdAt': '',
      'updatedAt': '',
    };
    this.hardwareSets = [newHardwareSet, ...this.hardwareSets];
  }

  showSuccess() {
    this.messageService.add({
      severity: 'success',
      life: 1000,
      summary: 'Success Update!',
      detail: 'Changes Successfully Applied',
    });
  }

  private handleError(err: Error) {
    console.error(err);
  }

  showInfoPopUp(header: string, message: string) {
    this.showPopUp('general', header, message, 'pi pi-info-circle');
  }

  showErrorPopUp(header: string, message: string) {
    this.showPopUp('error', header, message, 'pi pi-exclamation-triangle');
  }

  showPopUp(key: string, header: string, message: string, icon: string) {
    this.confirmationService.confirm({
      key: key,
      message: message,
      header: header,
      rejectVisible: false,
      acceptLabel:'OK',
      icon: icon,
      accept: () => {
      },
      reject: () => {
      }
    });
  }
}
